import { BrandMode, BrandSize, Button, ChevronUpIcon, CloseIcon, Modal } from '@postidigital/posti-components'
import { toJS } from 'mobx'
import React, { FC, useContext } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { StoreContext } from '../../store'
import { DropDownType } from '../../store/dataModels/interfaces'
import { ContentSection } from '../../style/layout'
import { arrayIsEmpty } from '../../utils/helpers'
import { MainUserRadioGroup } from '../general/MainUserRadioGroup'
import { FilterDropdown } from '../main/FilterDropdown'
import { FilterServicesList } from '../main/FilterServicesList'
import { StyledDialog, StyledModal } from '../Modal/Modal.style'

export const FilterModal: FC<{ handleToggleFilters: () => void; filterOpen: boolean }> = ({
  handleToggleFilters,
  filterOpen,
}) => {
  const { t } = useTranslation()
  const { layoutStore, searchStore } = useContext(StoreContext)
  const { isMobile } = layoutStore

  const {
    searchMainUsers,
    organisationsOptions,
    selectedOrganisations,
    customerNumbers,
    selectedCustomerNumbers,
    LSCN,
    selectedLSCN,
    transportIds,
    selectedTransportIds,
    principalIds,
    selectedPrincipalIds,
    toggleSearchMainUsers,
    setSelectedOrganisations,
    setSelectedCustomerNumbers,
    setSelectedLSCN,
    setSelectedTransportIds,
    setSelectedPrincipalIds,
    clearFilters,
  } = searchStore

  const orgsToJS = toJS(organisationsOptions)
  const selectedOrganisationsToJS = toJS(selectedOrganisations)
  const orgsSelected = !arrayIsEmpty(selectedOrganisationsToJS)

  const allOption = {
    label: t(`general.selectAll`),
    value: '*',
    type: DropDownType.SELECT_ALL,
  }

  if (!isMobile) {
    return null
  }

  return createPortal(
    <>
      <Modal.Backdrop isOpen={filterOpen} onClick={handleToggleFilters} />
      <StyledModal isFullscreen isOpen={filterOpen}>
        <StyledDialog>
          <ContentSection top="lg">
            <div>
              <Modal.CloseButton
                isOnlyIcon
                style={{ top: '1.75rem', right: '1.25rem' }}
                aria-label="Close"
                size={BrandSize.xs}
                onClick={handleToggleFilters}
                leftIcon={CloseIcon}
                contentMode={BrandMode.secondary}
              />
              <Button
                style={{ width: 'fit-content' }}
                hasBackground={false}
                rightIcon={CloseIcon}
                size="sm"
                contentMode="secondary"
                onClick={() => clearFilters()}
              >
                {t('filter.clearAll')}
              </Button>
            </div>

            <MainUserRadioGroup
              small
              plurarTranslations
              isMainUser={searchMainUsers}
              toggleUserRole={toggleSearchMainUsers}
            />

            <FilterDropdown
              id="organization_selector"
              key="organization_selector"
              options={orgsToJS}
              selectedOptions={selectedOrganisationsToJS}
              allowSelectAll
              isMulti
              allOption={allOption}
              placeHolderText={t('general.organisations')}
              onChange={setSelectedOrganisations}
            />

            {customerNumbers?.length > 0 && (
              <FilterDropdown
                id="customer_number_selector"
                key="customer_number_selector"
                disabled={!orgsSelected}
                options={toJS(customerNumbers)}
                selectedOptions={toJS(selectedCustomerNumbers)}
                allowSelectAll
                isMulti
                grouped
                allOption={allOption}
                placeHolderText={t('general.customerNumbers')}
                onChange={setSelectedCustomerNumbers}
              />
            )}

            {LSCN?.length > 0 && (
              <FilterDropdown
                id="lcsnumbers_selector"
                key="lcsnumbers_selector"
                disabled={!orgsSelected}
                options={toJS(LSCN)}
                selectedOptions={toJS(selectedLSCN)}
                allowSelectAll
                isMulti
                grouped
                allOption={allOption}
                placeHolderText={t('general.LCSNumbers')}
                onChange={setSelectedLSCN}
              />
            )}

            {transportIds?.length > 0 && (
              <FilterDropdown
                id="transportid_selector"
                key="transportid_selector"
                disabled={!orgsSelected}
                grouped
                options={transportIds}
                selectedOptions={toJS(selectedTransportIds)}
                allowSelectAll
                isMulti
                allOption={allOption}
                placeHolderText={t('general.transportIds')}
                onChange={setSelectedTransportIds}
              />
            )}

            {principalIds?.length > 0 && (
              <FilterDropdown
                id="principalid_selector"
                key="principalid_selector"
                disabled={!orgsSelected}
                grouped
                options={principalIds}
                selectedOptions={toJS(selectedPrincipalIds)}
                allowSelectAll
                isMulti
                allOption={allOption}
                placeHolderText={t('general.principalIds')}
                onChange={setSelectedPrincipalIds}
              />
            )}

            {orgsSelected && <FilterServicesList />}
          </ContentSection>
          <ContentSection top="lg">
            <StyledButton2 id="btn_close_filter" onClick={handleToggleFilters} rightIcon={ChevronUpIcon}>
              {t('filter.done')}
            </StyledButton2>
          </ContentSection>
        </StyledDialog>
      </StyledModal>
    </>,
    document.getElementById('root')!
  )
}

const StyledButton2 = styled(Button)`
  width: 100%;
`
